import { useState, useMemo, type ChangeEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ShoppingCart, Search } from 'lucide-react';
import { format } from 'date-fns';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  Skeleton,
  Table,
  useToast,
} from '../components';
import { PageHeader } from '../layouts/PageHeader';
import type {
  Booking,
  Client,
  Integrator,
  Order,
  OrderStatus,
  RobotModel,
} from '../types/api';
import { orderStatusColor } from '../types/api';
import { useT } from '../i18n';
import styles from './Orders.module.css';

const ORDER_STATUSES: OrderStatus[] = [
  'DRAFT', 'CONFIRMED', 'IN_PRODUCTION', 'SHIPPED', 'SOLD', 'CANCELLED',
];

interface CreateOrderForm {
  integratorId: string;
  clientId: string;
  robotModelId: string;
  quantity: number;
  unitPrice: number;
}

const emptyForm: CreateOrderForm = {
  integratorId: '',
  clientId: '',
  robotModelId: '',
  quantity: 1,
  unitPrice: 0,
};

function formatPrice(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function OrdersPage() {
  const { user } = useAuth();
  const { t } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isManufacturer = user?.role === 'MANUFACTURER';

  const [search, setSearch] = useState('');

  // ── Data ─────────────────────────────────────────────────────────
  const ordersQ = useQuery<Order[]>({
    queryKey: ['orders'],
    queryFn: () => api.get<Order[]>('/orders'),
  });
  const clientsQ = useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: () => api.get<Client[]>('/clients'),
  });
  const integratorsQ = useQuery<Integrator[]>({
    queryKey: ['integrators'],
    queryFn: () => api.get<Integrator[]>('/integrators'),
    // Integrator users don't need full integrators list (they only see their own
    // orders); skip the fetch unless we're manufacturer.
    enabled: isManufacturer,
  });
  const modelsQ = useQuery<RobotModel[]>({
    queryKey: ['robot-models'],
    queryFn: () => api.get<RobotModel[]>('/robot-models'),
  });
  const bookingsQ = useQuery<Booking[]>({
    queryKey: ['bookings', 'active'],
    queryFn: () => api.get<Booking[]>('/bookings', { query: { active: true } }),
  });

  const orders = ordersQ.data ?? [];

  const filteredOrders = useMemo(() => {
    if (!search.trim()) return orders;
    const q = search.trim().toLowerCase();
    return orders.filter((o) =>
      o.client?.companyName.toLowerCase().includes(q) ||
      o.robotModel?.name.toLowerCase().includes(q) ||
      o.robotModel?.sku.toLowerCase().includes(q),
    );
  }, [orders, search]);

  // ── Create flow ──────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState<CreateOrderForm>(emptyForm);

  const setField = <K extends keyof CreateOrderForm>(k: K, v: CreateOrderForm[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  // Client list depends on integrator scope.
  // - Manufacturer: integrator must be picked first; show only clients booked
  //   to that integrator (matches backend create-order restriction).
  // - Integrator: own integratorId → show only own booked clients.
  const scopedIntegratorId = isManufacturer
    ? form.integratorId
    : user?.integratorId ?? '';

  const bookedClientIds = useMemo(() => {
    if (!scopedIntegratorId) return new Set<string>();
    return new Set(
      (bookingsQ.data ?? [])
        .filter((b) => b.integratorId === scopedIntegratorId && b.active)
        .map((b) => b.clientId),
    );
  }, [bookingsQ.data, scopedIntegratorId]);

  const availableClients = useMemo(() => {
    const all = clientsQ.data ?? [];
    if (!scopedIntegratorId) return [];
    return all.filter((c) => bookedClientIds.has(c.id));
  }, [clientsQ.data, scopedIntegratorId, bookedClientIds]);

  const activeModels = useMemo(
    () => (modelsQ.data ?? []).filter((m) => m.active),
    [modelsQ.data],
  );

  const onSelectModel = (e: ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setField('robotModelId', id);
    // Default unit price to the model's base price if user hasn't entered one.
    const model = activeModels.find((m) => m.id === id);
    if (model && form.unitPrice === 0) {
      setField('unitPrice', model.basePrice);
    }
  };

  const onSelectIntegrator = (e: ChangeEvent<HTMLSelectElement>) => {
    setField('integratorId', e.target.value);
    setField('clientId', ''); // clients list resets
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateOrderForm) => {
      const body: Record<string, unknown> = {
        clientId: payload.clientId,
        robotModelId: payload.robotModelId,
        quantity: payload.quantity,
        unitPrice: payload.unitPrice,
      };
      if (isManufacturer && payload.integratorId) {
        body.integratorId = payload.integratorId;
      }
      return api.post<Order>('/orders', body);
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success(
        t('orders.toastCreated'),
        `${created.client?.companyName ?? t('orders.clientFallback')} · ${created.quantity} × ${created.robotModel?.sku ?? '—'}`,
      );
      setCreateOpen(false);
      setForm(emptyForm);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('orders.errorCreate');
      toast.error(t('orders.toastCreateFail'), msg);
    },
  });

  const submitCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (createMutation.isPending) return;
    createMutation.mutate(form);
  };

  const requiredOk =
    form.clientId &&
    form.robotModelId &&
    form.quantity > 0 &&
    form.unitPrice > 0 &&
    (!isManufacturer || form.integratorId);

  // ── Status change (inline) ───────────────────────────────────────
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) =>
      api.patch<Order>(`/orders/${id}`, { status }),
    onSuccess: (updated) => {
      // Update cache without a full refetch — keeps the select responsive.
      queryClient.setQueryData<Order[]>(['orders'], (prev) =>
        (prev ?? []).map((o) => (o.id === updated.id ? updated : o)),
      );
      toast.success(
        t('orders.toastStatusUpdated'),
        `${updated.client?.companyName ?? t('orders.orderFallback')} → ${t(`status.order.${updated.status}`)}`,
      );
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : t('orders.errorStatus');
      toast.error(t('orders.toastStatusFail'), msg);
    },
  });

  const onStatusChange = (orderId: string) => (e: ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value as OrderStatus;
    statusMutation.mutate({ id: orderId, status });
  };

  // ── Render ───────────────────────────────────────────────────────
  const isLoading = ordersQ.isLoading;
  const errorMsg = ordersQ.error instanceof Error ? ordersQ.error.message : null;
  const total = (qty: number, unit: number) => qty * unit;

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('orders.title')}
        description={isManufacturer ? t('orders.descAll') : t('orders.descOwn')}
        action={
          <Button leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
            {t('orders.new')}
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <Input
          aria-label={t('orders.searchAria')}
          placeholder={t('orders.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<Search size={16} />}
          fullWidth
          className={styles.search}
        />
      </div>

      {errorMsg && (
        <Card className={styles.error}>
          <p>{errorMsg}</p>
        </Card>
      )}

      {isLoading ? (
        <OrdersSkeleton isManufacturer={isManufacturer} />
      ) : filteredOrders.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ShoppingCart size={28} />}
            title={search.trim() ? t('orders.emptySearchTitle') : t('orders.emptyTitle')}
            description={
              search.trim()
                ? t('orders.emptySearchDesc')
                : isManufacturer
                ? t('orders.emptyDescManufacturer')
                : t('orders.emptyDescIntegrator')
            }
            action={
              !search.trim() && (
                <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
                  {t('orders.new')}
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>{t('orders.colClient')}</th>
              {isManufacturer && <th>{t('orders.colIntegrator')}</th>}
              <th>{t('orders.colModel')}</th>
              <th className="numeric">{t('orders.colQty')}</th>
              <th className="numeric">{t('orders.colUnitPrice')}</th>
              <th className="numeric">{t('orders.colTotal')}</th>
              <th>{t('orders.colStatus')}</th>
              <th>{t('orders.colCreated')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((o) => (
              <tr key={o.id}>
                <td className={styles.cellBold}>{o.client?.companyName ?? '—'}</td>
                {isManufacturer && <td>{o.integrator?.name ?? '—'}</td>}
                <td>
                  <div className={styles.modelCell}>
                    <span className={styles.modelName}>{o.robotModel?.name ?? '—'}</span>
                    {o.robotModel && (
                      <span className={styles.modelSku}>{o.robotModel.sku}</span>
                    )}
                  </div>
                </td>
                <td className="numeric">{o.quantity}</td>
                <td className="numeric">{formatPrice(o.unitPrice)}</td>
                <td className={`numeric ${styles.cellBold}`}>
                  {formatPrice(total(o.quantity, o.unitPrice))}
                </td>
                <td>
                  <InlineStatusSelect
                    value={o.status}
                    onChange={onStatusChange(o.id)}
                    disabled={statusMutation.isPending && statusMutation.variables?.id === o.id}
                  />
                </td>
                <td className={styles.cellDate}>
                  {format(new Date(o.createdAt), 'yyyy-MM-dd')}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* ── Create Order modal ─────────────────────────────────── */}
      <Modal
        open={createOpen}
        onClose={() => !createMutation.isPending && setCreateOpen(false)}
        title={t('orders.modalTitle')}
        description={t('orders.modalDesc')}
        size="md"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setCreateOpen(false)}
              disabled={createMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="create-order-form"
              loading={createMutation.isPending}
              disabled={!requiredOk}
            >
              {t('common.create')}
            </Button>
          </>
        }
      >
        <form id="create-order-form" onSubmit={submitCreate} className={styles.form} noValidate>
          {isManufacturer && (
            <Select
              label={t('orders.integratorLabel')}
              value={form.integratorId}
              onChange={onSelectIntegrator}
              placeholder={t('orders.selectIntegrator')}
              required
              fullWidth
              helperText={t('orders.onlyActiveIntegrators')}
              options={(integratorsQ.data ?? [])
                .filter((i) => i.active)
                .map((i) => ({ value: i.id, label: i.name }))}
            />
          )}

          <Select
            label={t('orders.clientLabel')}
            value={form.clientId}
            onChange={(e) => setField('clientId', e.target.value)}
            placeholder={
              isManufacturer && !form.integratorId
                ? t('orders.selectIntegratorFirst')
                : t('orders.selectClient')
            }
            required
            fullWidth
            disabled={isManufacturer && !form.integratorId}
            helperText={
              isManufacturer
                ? t('orders.clientHelperManufacturer')
                : t('orders.clientHelperIntegrator')
            }
            options={availableClients.map((c) => ({
              value: c.id,
              label: c.companyName,
            }))}
          />

          <Select
            label={t('orders.modelLabel')}
            value={form.robotModelId}
            onChange={onSelectModel}
            placeholder={t('orders.selectModel')}
            required
            fullWidth
            helperText={t('orders.modelHelper')}
            options={activeModels.map((m) => ({
              value: m.id,
              label: `${m.name} (${m.sku}) — ${formatPrice(m.basePrice)}`,
            }))}
          />

          <div className={styles.row}>
            <Input
              label={t('orders.quantity')}
              type="number"
              min={1}
              value={String(form.quantity)}
              onChange={(e) =>
                setField('quantity', Math.max(1, Number.parseInt(e.target.value, 10) || 1))
              }
              required
              fullWidth
            />
            <Input
              label={t('orders.unitPriceUsd')}
              type="number"
              min={0}
              step={100}
              value={String(form.unitPrice)}
              onChange={(e) =>
                setField('unitPrice', Number.parseFloat(e.target.value) || 0)
              }
              required
              fullWidth
            />
          </div>

          {form.robotModelId && form.quantity > 0 && form.unitPrice > 0 && (
            <div className={styles.totalCard}>
              <span className={styles.totalLabel}>{t('orders.orderTotal')}</span>
              <span className={styles.totalValue}>
                {formatPrice(total(form.quantity, form.unitPrice))}
              </span>
            </div>
          )}
        </form>
      </Modal>
    </div>
  );
}

// ── Inline status select on each row ──────────────────────────────

function InlineStatusSelect({
  value,
  onChange,
  disabled,
}: {
  value: OrderStatus;
  onChange: (e: ChangeEvent<HTMLSelectElement>) => void;
  disabled?: boolean;
}) {
  const { t } = useT();
  // Build classes from the status tone so the pill colour matches the row.
  const tone = orderStatusColor(value);
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`${styles.statusSelect} ${styles[`status_${tone}`]}`}
      aria-label={t('orders.changeStatus')}
    >
      {ORDER_STATUSES.map((s) => (
        <option key={s} value={s}>{t(`status.order.${s}`)}</option>
      ))}
    </select>
  );
}

function OrdersSkeleton({ isManufacturer }: { isManufacturer: boolean }) {
  const { t } = useT();
  const cols = isManufacturer ? 8 : 7;
  return (
    <Table>
      <thead>
        <tr>
          <th>{t('orders.colClient')}</th>
          {isManufacturer && <th>{t('orders.colIntegrator')}</th>}
          <th>{t('orders.colModel')}</th>
          <th className="numeric">{t('orders.colQty')}</th>
          <th className="numeric">{t('orders.colUnitPrice')}</th>
          <th className="numeric">{t('orders.colTotal')}</th>
          <th>{t('orders.colStatus')}</th>
          <th>{t('orders.colCreated')}</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 5 }).map((_, i) => (
          <tr key={i}>
            {Array.from({ length: cols }).map((__, j) => (
              <td key={j}><Skeleton width="80%" /></td>
            ))}
          </tr>
        ))}
      </tbody>
    </Table>
  );
}
